/**
 * HiringHub — recruiter-facing landing page for AI-generated hiring
 * assessments.
 *
 * Feb 2026 · R12.
 *
 * Recruiters paste a JD → pick sections → generate quiz → share the
 * public URL → get ranked candidates in the detail view.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Briefcase, Plus, Sparkles, Users, Copy, ExternalLink, Trash2, CheckCircle2, XCircle } from "lucide-react";

const SECTION_KINDS = [
  { key: "technical_mcq", label: "Technical MCQs", hint: "Role-specific knowledge (4-option MCQs)" },
  { key: "iq", label: "IQ / Reasoning", hint: "Pattern completion + verbal reasoning" },
  { key: "eq_scenario", label: "EQ Scenarios", hint: "Workplace vignettes, ranked responses" },
  { key: "short_answer", label: "Short Answer", hint: "Open-ended scenario / coding-in-prose" },
];

const CAPTURE_FIELDS = [
  { key: "phone", label: "Phone number" },
  { key: "linkedin_url", label: "LinkedIn URL" },
  { key: "resume_url", label: "Resume URL" },
  { key: "current_ctc", label: "Current CTC" },
  { key: "notice_period", label: "Notice period (days)" },
  { key: "years_experience", label: "Years of experience" },
];

const emptyCapture = () => Object.fromEntries(
  CAPTURE_FIELDS.map(f => [f.key, { enabled: false, required: false }])
);

export default function HiringHub() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [form, setForm] = useState(defaultForm());

  function defaultForm() {
    return {
      title: "",
      role_title: "",
      seniority: "mid",
      jd_text: "",
      required_skills_str: "",
      years_experience: "",
      sections: [
        { kind: "technical_mcq", question_count: 5 },
        { kind: "iq", question_count: 3 },
      ],
      time_limit_min: 45,
      cutoff_score: 60,
      prometric_mode: "strict",
      capture_config: emptyCapture(),
      notify_candidate: true,
      recruiter_email: "",
      expires_in_days: 30,
    };
  }

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/hiring/assessments`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  const totalQuestions = form.sections.reduce((sum, s) => sum + (s.question_count || 0), 0);

  const addSection = (kind) => {
    if (form.sections.some(s => s.kind === kind)) return;
    setForm(f => ({ ...f, sections: [...f.sections, { kind, question_count: 5 }] }));
  };
  const removeSection = (kind) => {
    setForm(f => ({ ...f, sections: f.sections.filter(s => s.kind !== kind) }));
  };
  const updateSectionCount = (kind, count) => {
    setForm(f => ({ ...f, sections: f.sections.map(s => s.kind === kind ? { ...s, question_count: Number(count) || 1 } : s) }));
  };
  const toggleCapture = (key, prop) => {
    setForm(f => {
      const cfg = { ...f.capture_config, [key]: { ...f.capture_config[key], [prop]: !f.capture_config[key][prop] } };
      // If we're turning off `enabled`, also unset `required`
      if (prop === "enabled" && !cfg[key].enabled) cfg[key].required = false;
      // If turning on `required`, ensure `enabled`
      if (prop === "required" && cfg[key].required) cfg[key].enabled = true;
      return { ...f, capture_config: cfg };
    });
  };

  const generate = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.role_title.trim()) return toast.error("Role title is required");
    if (form.jd_text.trim().length < 20) return toast.error("JD text must be at least 20 characters");
    if (form.sections.length === 0) return toast.error("Pick at least one section");
    if (totalQuestions < 3) return toast.error("Total questions must be at least 3");
    if (totalQuestions > 60) return toast.error("Total questions cannot exceed 60");
    setGenBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        role_title: form.role_title.trim(),
        seniority: form.seniority,
        jd_text: form.jd_text.trim(),
        required_skills: form.required_skills_str.split(",").map(s => s.trim()).filter(Boolean),
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        sections: form.sections,
        time_limit_min: Number(form.time_limit_min) || 45,
        cutoff_score: Number(form.cutoff_score) || 60,
        prometric_mode: form.prometric_mode,
        capture_config: form.capture_config,
        notify_recruiter_emails: form.recruiter_email ? [form.recruiter_email.trim()] : [],
        notify_candidate: form.notify_candidate,
        expires_in_days: Number(form.expires_in_days) || 30,
      };
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/hiring/assessments/generate`, payload
      );
      toast.success("Assessment created");
      setOpen(false);
      setForm(defaultForm());
      navigate(`/app/lms/hiring/${data.id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Generate failed"));
    } finally { setGenBusy(false); }
  };

  const copyLink = async (row) => {
    const url = `${window.location.origin}/pq/${row.share_token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Public link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const removeAssessment = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? Public link will be revoked immediately.`)) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/hiring/assessments/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="hiring-hub-page">
      <PageHeader
        title="Hiring Assessment Hub"
        description="AI-generate role-specific assessments from a job description. Share the public link and get a ranked candidate shortlist."
        actions={
          <Button className="rounded-none h-9" onClick={() => setOpen(true)} data-testid="hiring-new-btn">
            <Sparkles className="w-4 h-4 mr-1" /> New assessment
          </Button>
        }
      />

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="border-2 border-dashed border-neutral-200 p-10 text-center space-y-2">
          <Briefcase className="w-10 h-10 text-neutral-400 mx-auto" />
          <div className="font-medium">No assessments yet</div>
          <div className="text-sm text-muted-foreground">Paste a job description to have AI compose a role-specific assessment.</div>
          <Button className="rounded-none mt-2" onClick={() => setOpen(true)} data-testid="hiring-empty-cta">
            <Plus className="w-4 h-4 mr-1" /> Create your first assessment
          </Button>
        </div>
      )}
      {rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map(r => (
            <div key={r.id} className="border p-4 space-y-2 hover:border-neutral-500 transition-colors" data-testid={`hiring-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{r.role_title} · {r.seniority}</div>
                  <div className="font-semibold truncate">{r.title}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 border ${r.status === "closed" ? "border-neutral-400 text-neutral-500" : "border-emerald-400 text-emerald-600"}`}>{r.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center pt-1">
                <Stat icon={<Users className="w-3 h-3" />} label="Candidates" value={r.candidate_count || 0} />
                <Stat icon={<CheckCircle2 className="w-3 h-3 text-emerald-500" />} label="Passed" value={r.passed_count || 0} />
                <Stat icon={<Sparkles className="w-3 h-3 text-amber-500" />} label="Top" value={r.top_score !== null && r.top_score !== undefined ? `${r.top_score}%` : "—"} />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button size="sm" className="rounded-none flex-1" onClick={() => navigate(`/app/lms/hiring/${r.id}`)} data-testid={`hiring-view-${r.id}`}>View shortlist</Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => copyLink(r)} title="Copy public link" data-testid={`hiring-copy-${r.id}`}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => window.open(`/pq/${r.share_token}`, "_blank")} title="Open public link" data-testid={`hiring-open-${r.id}`}>
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="rounded-none text-rose-600 border-rose-300 hover:bg-rose-50" onClick={() => removeAssessment(r)} title="Delete" data-testid={`hiring-del-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => !genBusy && setOpen(v)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" /> New Hiring Assessment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Assessment title *</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Q1 Senior Backend Hiring" className="h-9 rounded-none" data-testid="hiring-form-title" />
              </div>
              <div>
                <Label className="text-xs">Role title *</Label>
                <Input value={form.role_title} onChange={e => setForm({ ...form, role_title: e.target.value })} placeholder="Senior Backend Engineer" className="h-9 rounded-none" data-testid="hiring-form-role" />
              </div>
              <div>
                <Label className="text-xs">Seniority</Label>
                <Select value={form.seniority} onValueChange={v => setForm({ ...form, seniority: v })}>
                  <SelectTrigger className="h-9 rounded-none" data-testid="hiring-form-seniority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["intern", "junior", "mid", "senior", "lead", "principal"].map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Years of experience</Label>
                <Input type="number" value={form.years_experience} onChange={e => setForm({ ...form, years_experience: e.target.value })} placeholder="4" className="h-9 rounded-none" data-testid="hiring-form-years" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Required skills (comma-separated)</Label>
              <Input value={form.required_skills_str} onChange={e => setForm({ ...form, required_skills_str: e.target.value })} placeholder="Python, FastAPI, MongoDB, React" className="h-9 rounded-none" data-testid="hiring-form-skills" />
            </div>

            <div>
              <Label className="text-xs">Job description *</Label>
              <Textarea value={form.jd_text} onChange={e => setForm({ ...form, jd_text: e.target.value })}
                rows={6}
                placeholder="Paste the JD here. Include responsibilities, tech stack, and any must-have skills…"
                className="rounded-none"
                data-testid="hiring-form-jd" />
              <div className="text-[10px] text-muted-foreground mt-1">{form.jd_text.length} chars (min 20)</div>
            </div>

            {/* Sections */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Assessment sections</Label>
                <div className="text-[10px] text-muted-foreground">Total: <b>{totalQuestions}</b> questions</div>
              </div>
              <div className="space-y-2">
                {SECTION_KINDS.map(sk => {
                  const active = form.sections.find(s => s.kind === sk.key);
                  return (
                    <div key={sk.key} className={`border p-2 flex items-center gap-2 ${active ? "border-emerald-400 bg-emerald-50/40" : "border-neutral-200"}`}>
                      <Switch checked={!!active} onCheckedChange={(v) => v ? addSection(sk.key) : removeSection(sk.key)} data-testid={`hiring-sec-${sk.key}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{sk.label}</div>
                        <div className="text-[10px] text-muted-foreground">{sk.hint}</div>
                      </div>
                      {active && (
                        <div className="flex items-center gap-1">
                          <Label className="text-[10px]">Q</Label>
                          <Input type="number" min="1" max="25" value={active.question_count} onChange={e => updateSectionCount(sk.key, e.target.value)} className="h-8 w-16 rounded-none" data-testid={`hiring-sec-${sk.key}-count`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Basic settings */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Time limit (min)</Label>
                <Input type="number" value={form.time_limit_min} onChange={e => setForm({ ...form, time_limit_min: e.target.value })} className="h-9 rounded-none" data-testid="hiring-form-time" />
              </div>
              <div>
                <Label className="text-xs">Cutoff score (%)</Label>
                <Input type="number" value={form.cutoff_score} onChange={e => setForm({ ...form, cutoff_score: e.target.value })} className="h-9 rounded-none" data-testid="hiring-form-cutoff" />
              </div>
              <div>
                <Label className="text-xs">Proctoring</Label>
                <Select value={form.prometric_mode} onValueChange={v => setForm({ ...form, prometric_mode: v })}>
                  <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strict">Strict (auto-submit)</SelectItem>
                    <SelectItem value="moderate">Moderate (warn only)</SelectItem>
                    <SelectItem value="off">Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Candidate capture fields */}
            <div>
              <Label className="text-xs">Candidate info to capture on the public form</Label>
              <div className="text-[10px] text-muted-foreground mb-2">Name & email are always captured. Toggle extra fields per assessment.</div>
              <div className="border p-2 space-y-1">
                {CAPTURE_FIELDS.map(cf => (
                  <div key={cf.key} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-xs">
                    <div>{cf.label}</div>
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={form.capture_config[cf.key].enabled} onChange={() => toggleCapture(cf.key, "enabled")} data-testid={`hiring-cap-${cf.key}-en`} />
                      Ask
                    </label>
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={form.capture_config[cf.key].required} onChange={() => toggleCapture(cf.key, "required")} data-testid={`hiring-cap-${cf.key}-req`} />
                      Required
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Recruiter email (for alerts)</Label>
                <Input type="email" value={form.recruiter_email} onChange={e => setForm({ ...form, recruiter_email: e.target.value })} placeholder="you@company.com" className="h-9 rounded-none" data-testid="hiring-form-recruiter" />
              </div>
              <div>
                <Label className="text-xs">Link expires (days)</Label>
                <Input type="number" value={form.expires_in_days} onChange={e => setForm({ ...form, expires_in_days: e.target.value })} className="h-9 rounded-none" data-testid="hiring-form-expiry" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.notify_candidate} onChange={e => setForm({ ...form, notify_candidate: e.target.checked })} />
              Send confirmation email to candidate on submission
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)} disabled={genBusy}>Cancel</Button>
            <Button className="rounded-none" onClick={generate} disabled={genBusy} data-testid="hiring-form-generate">
              {genBusy ? (
                <><Sparkles className="w-4 h-4 mr-1 animate-pulse" /> Generating…</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-1" /> Generate assessment</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="border p-2">
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">{icon}{label}</div>
      <div className="font-mono text-lg font-semibold">{value}</div>
    </div>
  );
}
